import React, { useMemo } from "react";
import { FaCrown, FaSpinner } from "react-icons/fa";

import {
  daysArray,
  getAdherentName,
  getMonthlyCellMeta,
} from "../utils/attendanceUtils";

function getSavingKey(adherentId, day) {
  return `${adherentId}-${day}`;
}

export default function AttendanceMonthlyHeatmapTable({
  month,
  rows = [],
  highlightIds = [],
  savingCells = new Set(),
  onToggleCell,
}) {
  const days = useMemo(() => (month ? daysArray(month) : []), [month]);

  const highlightSet = useMemo(
    () => new Set((highlightIds || []).map(Number)),
    [highlightIds],
  );

  if (!month || rows.length === 0) return null;

  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/40">
        <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-slate-200 bg-slate-50 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                Judoka
              </th>

              {days.map((day) => (
                <th
                  key={day}
                  className="border-b border-r border-slate-200 bg-slate-50 px-1 py-3 text-center text-xs font-bold text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400"
                >
                  {day}
                </th>
              ))}

              <th className="border-b border-slate-200 bg-slate-50 px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const isTop = highlightSet.has(Number(row.id));

              return (
                <tr
                  key={row.id}
                  className="transition hover:bg-slate-50 dark:hover:bg-slate-900/70"
                >
                  <td
                    className={`sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-950 ${
                      isTop ? "bg-amber-50 dark:bg-amber-900/20" : ""
                    }`}
                  >
                    <div className="flex min-w-[220px] items-center gap-2">
                      {isTop && (
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-amber-400 text-white">
                          <FaCrown className="text-xs" />
                        </span>
                      )}

                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                          {getAdherentName(row.adherent)}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {row.totalPresent} présent
                          {row.totalPresent > 1 ? "s" : ""} ·{" "}
                          {row.totalAbsent} absent
                          {row.totalAbsent > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </td>

                  {days.map((day) => {
                    const value = row.dates?.[day];
                    const meta = getMonthlyCellMeta(value);
                    const saving = savingCells.has(getSavingKey(row.id, day));

                    return (
                      <td
                        key={day}
                        className="border-b border-r border-slate-200 px-1 py-2 text-center dark:border-slate-800"
                        title={meta.label}
                      >
                        <button
                          type="button"
                          onClick={() => onToggleCell?.(row.id, day, value)}
                          disabled={saving}
                          aria-label={`${meta.label} — cliquer pour changer`}
                          className={`mx-auto grid h-7 w-7 place-items-center rounded-lg text-[10px] font-bold ring-1 transition focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:cursor-wait disabled:opacity-70 ${meta.className}`}
                        >
                          {saving ? (
                            <FaSpinner className="animate-spin text-[10px]" />
                          ) : (
                            meta.short
                          )}
                        </button>
                      </td>
                    );
                  })}

                  <td className="border-b border-slate-200 px-3 py-2 text-center dark:border-slate-800">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      {row.totalPresent}
                    </div>

                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                      {row.rate}%
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {highlightIds.length > 1 && (
        <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
          Plusieurs judokas sont ex æquo à la meilleure assiduité ce mois-ci.
        </p>
      )}
    </div>
  );
}